/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX } from "react";
import { useEffect, useRef } from "react";
import { Outlet } from "react-router-dom";

import { usePictureInPictureCoordinator } from "../../VideoPlayerUI/pictureInPicture";
import { Log, LocationUrls } from "../../../viewModel";
import { useNavigationService } from "../../hooks";
import { useUnityDispatch, useUnitySelector } from "../../../viewModel";
import {
  loadProfiles,
  loadActiveProfileSelectionPreference,
  saveActiveProfileSelectionPreference,
  setSelectedProfileId,
  setIsProfileSelected,
  setSelectedProfile,
  isProfileError,
  PROFILE_ERROR_OPERATIONS,
  selectProfilesResponse,
  showProfileErrorDialog,
  type TProfileListResponse,
  type TActiveProfileSelection
} from "../../../viewModel/profiles";
import {
  selectHasStartedUp,
  selectIsProfileGateResolved,
  selectRequiresProfileSelection
} from "../../../viewModel/postSigninStartup";

const TAG: string = "MainLayout";

/**
 * Last-resort profile gate, run only after the startup sequence has finished
 * without resolving one — never alongside it.
 *
 * Mirrors the sequence's own `resolveProfileGate`: load the saved preference,
 * and only fetch profiles when there is none, since the profile count is
 * needed only to decide between redirecting and auto-selecting.
 */
async function resolveProfileGateFallback(
  dispatch: ReturnType<typeof useUnityDispatch>,
  navigateTo: (url: string, replace?: boolean) => void,
  cachedProfiles: TProfileListResponse | null,
  handleProfileError: (error: unknown) => void
): Promise<void> {
  let preference: TActiveProfileSelection | null = null;
  try {
    preference = await dispatch(
      loadActiveProfileSelectionPreference()
    ).unwrap();
  } catch (error: unknown) {
    Log.e(TAG, "loadActiveProfileSelectionPreference()", error);
    handleProfileError(error);
  }

  // A valid saved selection means there is nothing to choose.
  if (preference !== null && preference.activeProfileId !== "") {
    return;
  }

  // Reuse whatever Step 4 already put in the store. It only failed to *record*
  // the gate result; if the profiles themselves landed, refetching them is the
  // duplicate call this change exists to remove.
  let profilesResponse: TProfileListResponse | null = cachedProfiles;
  if (profilesResponse === null) {
    try {
      profilesResponse = await dispatch(loadProfiles()).unwrap();
    } catch (error: unknown) {
      Log.e(TAG, "loadProfiles()", error);
      handleProfileError(error);
      return;
    }
  }

  const profiles = profilesResponse.profiles;
  if (profiles === null || profiles === undefined) {
    return;
  }

  if (profiles.length > 1) {
    navigateTo(LocationUrls.SELECT_PROFILE, true);
    return;
  }

  const firstProfile = profiles.length === 1 ? profiles[0] : undefined;
  if (firstProfile === null || firstProfile === undefined) {
    return;
  }

  try {
    await dispatch(
      setSelectedProfile({ profileId: firstProfile.profileId })
    ).unwrap();
    await dispatch(
      saveActiveProfileSelectionPreference({
        activeProfileId: firstProfile.profileId,
        isSkipSelectedProfileEnabled: false
      })
    ).unwrap();
    dispatch(setSelectedProfileId(firstProfile.profileId));
    dispatch(setIsProfileSelected(true));
  } catch (error: unknown) {
    Log.e(TAG, "saveActiveProfileSelectionPreference()", error);
    Log.e(TAG, "resolveProfileGate() setSelectedProfile()", error);
    handleProfileError(error);
  }
}

/**
 * Layout wrapper for authenticated routes (MainScreen and future screens).
 *
 * Acts on the profile gate that `postSigninStartupSequence` resolves at its
 * Step 4: when the sequence reports more than one profile and no valid saved
 * selection, the user is redirected to `/select-profile` from whichever
 * protected route they landed on.
 *
 * ## Why it no longer resolves the gate itself
 *
 * This used to run its own copy of the gate on mount — load the saved
 * preference, fetch profiles if it was missing, auto-select a sole profile,
 * persist it — as a cold-entry fallback for a hard reload or direct
 * navigation. But `ProtectedRoute` dispatches the startup sequence on exactly
 * those entries too, so the fallback did not replace the sequence's Step 4, it
 * raced it: both fetched `/v1/profiles`, and this one usually won because it
 * mounts immediately while Step 4 waits behind device features.
 *
 * That race is what put a profiles request on the wire before the
 * `deviceFeatures` response that carries `msoServiceId` had arrived. The
 * sequence owns the gate now; this reads the answer, and only falls back to
 * resolving it once the sequence has finished without doing so.
 *
 * Also hosts the persistent PiP routing side-effect hook (no-op on SmartTV,
 * where native browser PiP is unsupported).
 */
const MainLayout: FunctionComponent = (): JSX.Element => {
  usePictureInPictureCoordinator();

  const { navigateTo } = useNavigationService();
  const dispatch = useUnityDispatch();

  const isProfileGateResolved: boolean = useUnitySelector(
    selectIsProfileGateResolved
  );
  const requiresProfileSelection: boolean = useUnitySelector(
    selectRequiresProfileSelection
  );
  const hasStartedUp: boolean = useUnitySelector(selectHasStartedUp);
  const profilesResponse = useUnitySelector(selectProfilesResponse);

  const hasActedRef = useRef<boolean>(false);

  useEffect((): void => {
    if (hasActedRef.current) {
      return;
    }

    // The sequence answered. Act on it and we are done.
    if (isProfileGateResolved) {
      hasActedRef.current = true;
      if (requiresProfileSelection) {
        Log.d(TAG, "profile gate requires selection — redirecting");
        navigateTo(LocationUrls.SELECT_PROFILE, true);
      }
      return;
    }

    // Backstop. `resolveProfileGate` reports `isProfileGateResolved: false`
    // when its own profile load threw, so a finished sequence that still has
    // no answer means Step 4 failed. Waiting for `hasStartedUp` is what keeps
    // this from racing Step 4 the way the old on-mount version did.
    if (!hasStartedUp) {
      return;
    }

    hasActedRef.current = true;
    Log.w(TAG, "startup finished without resolving profile gate — retrying");
    const retryProfileGate = (): void => {
      const handleProfileError = (error: unknown): void => {
        Log.e(TAG, "resolveProfileGateFallback()", error);
        if (isProfileError(error)) {
          dispatch(
            showProfileErrorDialog({
              error: error,
              onRetry: retryProfileGate,
              operation: PROFILE_ERROR_OPERATIONS.LOAD
            })
          );
        }
      };

      resolveProfileGateFallback(
        dispatch,
        navigateTo,
        profilesResponse,
        handleProfileError
      ).catch(handleProfileError);
    };
    retryProfileGate();
  }, [
    dispatch,
    hasStartedUp,
    isProfileGateResolved,
    navigateTo,
    profilesResponse,
    requiresProfileSelection
  ]);

  return <Outlet />;
};

export default MainLayout;
